import React, { useMemo, useState } from 'react';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

/**
 * Maturité des commissions, éditeur par éditeur.
 *
 * ## Pourquoi cette vue existe
 *
 * Le tableau des éditeurs dit **combien** chacun a rapporté ; il ne dit pas ce qui est acquis.
 * Or une commission passe par trois états — en attente, validée, annulée — et le passage peut
 * prendre des semaines. Deux éditeurs au même montant n'ont donc pas la même valeur : l'un peut
 * être intégralement validé, l'autre entièrement suspendu à une vérification.
 *
 * ## Pourquoi des parts et non des montants
 *
 * Les montants s'étalent d'un facteur cinquante d'un éditeur à l'autre. En valeur absolue, le
 * plus gros écrase la barre et les autres deviennent des traits : on ne lit plus rien de ce
 * qu'on est venu voir, c'est-à-dire la **composition**. Chaque barre vaut donc 100 %, et le
 * montant total s'écrit à côté — la structure se compare, le poids reste lisible.
 *
 * ## Couleurs
 *
 * Palette de statut, jamais celle des séries : validé, en attente, annulé sont des états, pas
 * des catégories interchangeables. Sur fond clair, l'ambre passe sous le rapport de contraste
 * de 3:1 — c'est assumé, à condition que la couleur ne porte jamais seule le sens. D'où
 * l'icône et le libellé dans la légende, et les parts écrites sur les segments assez larges
 * pour les recevoir.
 */

const STATUTS = [
  { cle: 'approved', libelle: 'Validé', couleur: '#0ca30c', Icone: CheckCircle2 },
  { cle: 'pending', libelle: 'En attente', couleur: '#fab219', Icone: Clock },
  { cle: 'declined', libelle: 'Annulé', couleur: '#d03b3b', Icone: XCircle }
];

const euros = (v) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0
}).format(v || 0);

const eurosPrecis = (v) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR'
}).format(v || 0);

const part = (v) => `${Math.round(v)} %`;

const AwinCommissionStatus = ({
  editeurs = [],
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'awin_commission_status'
}) => {
  const [survole, setSurvole] = useState(null);

  /**
   * Les primes rejoignent le validé : ce sont des versements acquis, pas un état d'attente.
   * Les rassembler évite un quatrième segment qui vaudrait zéro sur presque tous les comptes.
   */
  const lignes = useMemo(() => editeurs
    .map(e => {
      const approved = (e.commApproved || 0) + (e.commBonus || 0);
      const pending = e.commPending || 0;
      const declined = e.commDeclined || 0;
      const total = approved + pending + declined;
      return { nom: e.publisherName, id: e.publisherId, approved, pending, declined, total };
    })
    .filter(l => l.total > 0)
    .sort((a, b) => b.total - a.total),
    [editeurs]);

  if (lignes.length === 0) return null;

  const totaux = lignes.reduce((acc, l) => ({
    approved: acc.approved + l.approved,
    pending: acc.pending + l.pending,
    declined: acc.declined + l.declined
  }), { approved: 0, pending: 0, declined: 0 });
  const totalGeneral = totaux.approved + totaux.pending + totaux.declined;

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4>
          <Clock size={16} />&nbsp;Statut des commissions
        </h4>
        {toggleClientModuleVisibility && (
          <ModuleVisibilityToggle
            isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
            onToggle={toggleClientModuleVisibility}
            userRole={userRole}
            moduleId={moduleId}
            title="Statut des commissions"
          />
        )}
      </div>

      {/* Légende : l'icône et le mot portent le sens, la couleur ne fait que le rappeler. */}
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', padding: '4px 0 18px' }}>
        {STATUTS.map(({ cle, libelle, couleur, Icone }) => (
          <span key={cle} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            fontSize: '13px', color: '#4b5563'
          }}>
            <Icone size={14} style={{ color: couleur }} />
            {libelle}
            <span style={{ color: '#9ca3af' }}>
              {euros(totaux[cle])}
              {totalGeneral > 0 && ` · ${part((totaux[cle] / totalGeneral) * 100)}`}
            </span>
          </span>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {lignes.map(ligne => (
          <div key={ligne.id}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              marginBottom: '6px', gap: '12px'
            }}>
              <span style={{
                fontSize: '13px', color: '#374151', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap'
              }}>
                {ligne.nom}
              </span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827', flexShrink: 0 }}>
                {eurosPrecis(ligne.total)}
              </span>
            </div>

            {/* La barre : 2 px de fond entre les segments plutôt qu'une bordure. */}
            <div style={{ display: 'flex', gap: '2px', height: '18px' }}>
              {STATUTS.map(({ cle, libelle, couleur }, i) => {
                const valeur = ligne[cle];
                if (valeur <= 0) return null;

                const pourcent = (valeur / ligne.total) * 100;
                const premier = STATUTS.slice(0, i).every(s => ligne[s.cle] <= 0);
                const dernier = STATUTS.slice(i + 1).every(s => ligne[s.cle] <= 0);
                const actif = survole && survole.id === ligne.id && survole.cle === cle;

                return (
                  <div
                    key={cle}
                    onMouseEnter={() => setSurvole({ id: ligne.id, cle, valeur, libelle, pourcent })}
                    onMouseLeave={() => setSurvole(null)}
                    title={`${libelle} — ${eurosPrecis(valeur)} (${part(pourcent)})`}
                    style={{
                      width: `${pourcent}%`,
                      background: couleur,
                      borderRadius: `${premier ? 4 : 0}px ${dernier ? 4 : 0}px ${dernier ? 4 : 0}px ${premier ? 4 : 0}px`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      opacity: actif ? 1 : 0.92,
                      transition: 'opacity 0.15s',
                      cursor: 'default'
                    }}
                  >
                    {/* Le libellé n'entre que s'il tient : sinon il serait tronqué au premier
                        caractère, et l'infobulle le porte déjà. */}
                    {pourcent >= 14 && (
                      <span style={{
                        fontSize: '11px', fontWeight: 600, color: 'white',
                        textShadow: '0 1px 2px rgba(0,0,0,0.25)'
                      }}>
                        {part(pourcent)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/*
        Ce que le graphe ne peut pas dire, et qu'il faut dire : une part élevée d'attente sur
        une période récente est normale, et ne présage pas d'un refus.
      */}
      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '18px 0 0 0', lineHeight: 1.5 }}>
        La validation intervient plusieurs semaines après la vente : sur une période récente,
        l'essentiel des commissions est normalement en attente.
      </p>
    </div>
  );
};

export default AwinCommissionStatus;
